import CronGenerator from 'components/CronGenerator/CronGenerator';
import Text from 'components/Text/Text';
import { AppContext, IAppContext } from 'appState/appContext';
import PosNameField from './components/PosNameField';
import { convertUndefinedFields } from 'utils/helpers';
import ActivationScheduleInput from './components/ActivationScheduleInput';
import DeactivationScheduleInput from './components/DeactivationScheduleInput';
import React, { useContext, useEffect } from 'react';
import { ServerErrorTypes, IRestaurantForCreateDish } from 'types';
import axios from 'axios';
import { Form } from 'antd';
import CustomerSelect from './components/CustomerField';
import RestaurantsField from './components/RestaurantsField/RestaurantsField';
import ExternalIdField from './components/ExternalIdField';
import Button from 'components/Button/Button';
import BtnBlock from './components/BtnBlock';
import DishNameField from './components/DishNameField';
import DescriptionField from './components/DescriptionField';
import IsDisabledField from './components/IsDisabledField/IsDisabledField';
import SuggestedDishesField from './components/SuggestedDishesField/SuggestedDishesField';
import AdditionalDishesField from './components/AdditionalIngredientsField/AdditionalDishesField';
import DishModificationsField from './components/DishModificationsField/DishModificationsField';
import RestaurantsForComments from './components/RestaurantsForComments/RestaurantsForComments';
import { CreateDishContext, ICreateDishContext } from '../../../../../createDishState/createDishContext';
import DOMPurify from 'dompurify';
import { preventDefaultForEnterKey } from 'utils/helpers';
import { createDish } from 'backend/dishes';
import classNames from 'classnames';
import style from './InputFields.module.scss';

const InputFields: React.FC = () => {
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);
	const {
		changeTypeOfModalWindow,
		showErrorModalWithInfo,
		changeDishName,
		setCustomer,
		setFetchedCustomers,
		changeShortDescription,
		setRestaurantsForPrice,
		setFetchedRestaurants,
		changePrice,
		addPrices,
		removePrice,
		setRestaurantWithTurnOffComment,
		setRestaurantWithRequiredComment,
		setDisabledInRestaurants,
		setSuggestedDishCategory,
		setFetchedSuggestedDishSubCategories,
		setSuggestedDishSubCategory,
		setSuggestedDishes,
		setFetchedSuggestedDishes,
		setAdditionalDishCategory,
		setAdditionalDishSubCategory,
		setFetchedAdditionalDishSubCategories,
		setAdditionalDishes,
		setFetchedAdditionalDishes,
		setDishModificationCategory,
		setDishModificationSubCategory,
		setFetchedDishModificationSubCategories,
		setDishModifications,
		setFetchedDishModifications,
		setIDsShowInComboDishes,
		changeExternalId,
		generateUniqueExternalId,
		removeSuggestedDish,
		removeAdditionalDish,
		removeDishModification,
		setFetchedCategories,
		changeActivationSchedule,
		changeDeactivationSchedule,
		fileId,
		name,
		shortDescription,
		selectedCustomer,
		restaurantsForPrice,
		price,
		totalPrices,
		restaurantWithTurnOffComment,
		restaurantWithRequiredComment,
		isDisabledInRestaurants,
		translations,
		activationSchedule,
		deactivationSchedule,
		selectedSuggestedDishCategory,
		selectedSuggestedDishSubCategory,
		selectedSuggestedDishes,
		selectedAdditionalDishCategory,
		selectedAdditionalDishSubCategory,
		selectedAdditionalDishes,
		selectedDishModificationCategory,
		selectedDishModificationSubCategory,
		selectedDishModifications,
		IDsShowInComboDishes,
		externalId,
		fetchedRestaurants,
		fetchedSuggestedDishes,
		fetchedAdditionalDishes,
		fetchedDishModifications,
		defaultCustomerLanguage,
	} = useContext<ICreateDishContext>(CreateDishContext);
	const [form] = Form.useForm();
	const restaurantCommentsStyles = classNames(style.row, style.flexStart);

	useEffect(() => {
		form.setFieldsValue({ name });
		form.setFieldsValue({ selectedCustomer });
		form.setFieldsValue({ restaurantsForPrice });
		form.setFieldsValue({ price });
		form.setFieldsValue({ restaurantWithTurnOffComment });
		form.setFieldsValue({ restaurantWithRequiredComment });
		form.setFieldsValue({ isDisabledInRestaurants });
		form.setFieldsValue({ activationSchedule });
		form.setFieldsValue({ deactivationSchedule });
		form.setFieldsValue({ selectedSuggestedDishCategory });
		form.setFieldsValue({ selectedSuggestedDishSubCategory });
		form.setFieldsValue({ selectedSuggestedDishes });
		form.setFieldsValue({ selectedAdditionalDishCategory });
		form.setFieldsValue({ selectedAdditionalDishSubCategory });
		form.setFieldsValue({ selectedAdditionalDishes });
		form.setFieldsValue({ selectedDishModificationCategory });
		form.setFieldsValue({ selectedDishModificationSubCategory });
		form.setFieldsValue({ selectedDishModifications });
		form.setFieldsValue({ externalId });
	}, [
		name,
		selectedCustomer,
		restaurantsForPrice,
		price,
		restaurantWithTurnOffComment,
		restaurantWithRequiredComment,
		isDisabledInRestaurants,
		activationSchedule,
		deactivationSchedule,
		selectedSuggestedDishCategory,
		selectedSuggestedDishSubCategory,
		selectedSuggestedDishes,
		selectedAdditionalDishCategory,
		selectedAdditionalDishSubCategory,
		selectedAdditionalDishes,
		selectedDishModificationCategory,
		selectedDishModificationSubCategory,
		selectedDishModifications,
		externalId,
	]);

	const submit = async () => {
		const defaultTranslationIsEmpty =
			translations.find((translation) => translation.languageId === defaultCustomerLanguage?.id)?.name === '';
		if (defaultTranslationIsEmpty) {
			changeTypeOfModalWindow('defaultTranslationIsEmpty');
			return;
		}

		const finalRestaurants: IRestaurantForCreateDish[] = totalPrices.map((el) => {
			return {
				restaurantId: el.restaurantId,
				price: el.price ? Number(el.price) : 0,
				isDisabled: isDisabledInRestaurants.includes(el.restaurantId),
				isCommentTurnOff: restaurantWithTurnOffComment.includes(el.restaurantId),
				isCommentRequired: restaurantWithRequiredComment.includes(el.restaurantId),
			};
		});

		const finalDishModifications = selectedDishModifications.map((id) => ({
			modificationId: id,
			showInCombo: IDsShowInComboDishes[id],
		}));

		try {
			changeTypeOfModalWindow(null);
			const convertedData = convertUndefinedFields({
				name,
				shortDescription: shortDescription ? DOMPurify.sanitize(shortDescription) : null,
				customerId: selectedCustomer || '',
				fileId,
				activationCron: activationSchedule,
				deactivationCron: deactivationSchedule,
				dishRestaurants: finalRestaurants,
				suggestedDishIds: selectedSuggestedDishes,
				additionalDishIds: selectedAdditionalDishes,
				dishModifications: finalDishModifications,
				externalId,
				translations,
			});
			showLoader();
			await createDish(convertedData);
			changeTypeOfModalWindow('success');
		} catch (err) {
			if (
				axios.isAxiosError(err) &&
				[
					ServerErrorTypes.SUGGESTED_DISH_VALIDATION_FAILED,
					ServerErrorTypes.ADDITIONAL_DISH_VALIDATION_FAILED,
					ServerErrorTypes.DISH_MODIFICATION_VALIDATION_FAILED,
				].includes(err.response?.data.Type)
			) {
				showErrorModalWithInfo({ ...err.response?.data, errorType: err.response?.data.Type });
			} else {
				changeTypeOfModalWindow('error');
			}
		} finally {
			hideLoader();
		}
	};

	const revalidateForm = form.validateFields;

	return (
		<div className={style.container} data-test="InputFields">
			<Form form={form} name="basic" onFinish={submit} onKeyPress={preventDefaultForEnterKey} layout="vertical">
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="mainInfoHeading"
						theme="secondary"
						size="l"
						weight="bold"
						display="inline"
					/>
					<DishNameField changeDishName={changeDishName} />
					<CustomerSelect setCustomer={setCustomer} setFetchedCustomers={setFetchedCustomers} />
				</div>
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="descriptionHeading"
						theme="secondary"
						size="l"
						weight="bold"
					/>
					<DescriptionField
						changeShortDescription={changeShortDescription}
						shortDescription={shortDescription}
					/>
				</div>
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="posNameLabel"
						theme="secondary"
						size="l"
						weight="bold"
					/>
					<PosNameField />
				</div>
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="restaurantsHeading"
						theme="secondary"
						size="l"
						weight="bold"
					/>
					<RestaurantsField
						restaurantsForPrice={restaurantsForPrice}
						totalPrices={totalPrices}
						selectedCustomer={selectedCustomer}
						price={price}
						fetchedRestaurants={fetchedRestaurants}
						revalidateForm={revalidateForm}
						setFetchedRestaurants={setFetchedRestaurants}
						setRestaurantsForPrice={setRestaurantsForPrice}
						changePrice={changePrice}
						removePrice={removePrice}
						addPrices={addPrices}
					/>
				</div>
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="idHeading"
						theme="secondary"
						size="l"
						weight="bold"
					/>
					<div className={style.row}>
						<ExternalIdField changeExternalId={changeExternalId} />
						<Button onClick={generateUniqueExternalId} theme="primary">
							<Text
								translationNameSpace="form"
								translationKey="generateUniqueExternalId"
								asFragment={true}
							/>
						</Button>
					</div>
				</div>
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="inStopHeading"
						theme="secondary"
						size="l"
						weight="bold"
					/>
					<IsDisabledField
						isDisabledInRestaurants={isDisabledInRestaurants}
						fetchedRestaurants={fetchedRestaurants}
						totalPrices={totalPrices}
						setDisabledInRestaurants={setDisabledInRestaurants}
					/>
				</div>
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="commentsHeading"
						theme="secondary"
						size="l"
						weight="bold"
					/>
					<div className={restaurantCommentsStyles}>
						<RestaurantsForComments
							setIDsRestaurant={setRestaurantWithTurnOffComment}
							IDSelectedCustomer={selectedCustomer}
							IDsSelectedRestaurant={restaurantWithTurnOffComment}
							restaurantsToExclude={restaurantWithRequiredComment}
							totalPrices={totalPrices}
							inputName="restaurantWithTurnOffComment"
							inputLabel="turnOffCommentLabel"
						/>
						<RestaurantsForComments
							setIDsRestaurant={setRestaurantWithRequiredComment}
							IDSelectedCustomer={selectedCustomer}
							IDsSelectedRestaurant={restaurantWithRequiredComment}
							restaurantsToExclude={restaurantWithTurnOffComment}
							totalPrices={totalPrices}
							inputName="restaurantWithRequiredComment"
							inputLabel="requiredCommentLabel"
						/>
					</div>
				</div>
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="scheduleHeading"
						theme="secondary"
						size="l"
						weight="bold"
					/>
					<div className={style.row}>
						<ActivationScheduleInput changeActivationSchedule={changeActivationSchedule} />
						<DeactivationScheduleInput changeDeactivationSchedule={changeDeactivationSchedule} />
					</div>
					<CronGenerator />
				</div>
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="suggestedDishesHeading"
						theme="secondary"
						size="l"
						weight="bold"
					/>
					<SuggestedDishesField
						selectedSuggestedDishes={selectedSuggestedDishes}
						fetchedSuggestedDishes={fetchedSuggestedDishes}
						totalPrices={totalPrices}
						selectedSuggestedDishCategory={selectedSuggestedDishCategory}
						selectedSuggestedDishSubCategory={selectedSuggestedDishSubCategory}
						removeSuggestedDish={removeSuggestedDish}
						setSuggestedDishes={setSuggestedDishes}
						setFetchedSuggestedDishes={setFetchedSuggestedDishes}
						setSuggestedDishSubCategory={setSuggestedDishSubCategory}
						setFetchedSuggestedDishSubCategories={setFetchedSuggestedDishSubCategories}
						setFetchedCategories={setFetchedCategories}
						setSuggestedDishCategory={setSuggestedDishCategory}
					/>
				</div>
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="additionalIngredientsHeading"
						theme="secondary"
						size="l"
						weight="bold"
					/>
					<AdditionalDishesField
						selectedAdditionalDishes={selectedAdditionalDishes}
						fetchedAdditionalDishes={fetchedAdditionalDishes}
						selectedAdditionalDishCategory={selectedAdditionalDishCategory}
						selectedAdditionalDishSubCategory={selectedAdditionalDishSubCategory}
						totalPrices={totalPrices}
						removeAdditionalDish={removeAdditionalDish}
						setAdditionalDishes={setAdditionalDishes}
						setFetchedAdditionalDishes={setFetchedAdditionalDishes}
						setFetchedAdditionalDishSubCategories={setFetchedAdditionalDishSubCategories}
						setAdditionalDishSubCategory={setAdditionalDishSubCategory}
						setAdditionalDishCategory={setAdditionalDishCategory}
					/>
				</div>
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="dishModificationsHeading"
						theme="secondary"
						size="l"
						weight="bold"
					/>
					<DishModificationsField
						selectedDishModifications={selectedDishModifications}
						fetchedDishModifications={fetchedDishModifications}
						selectedDishModificationCategory={selectedDishModificationCategory}
						selectedDishModificationSubCategory={selectedDishModificationSubCategory}
						totalPrices={totalPrices}
						removeDishModification={removeDishModification}
						setDishModifications={setDishModifications}
						setFetchedDishModifications={setFetchedDishModifications}
						setFetchedDishModificationSubCategories={setFetchedDishModificationSubCategories}
						setDishModificationSubCategory={setDishModificationSubCategory}
						setDishModificationCategory={setDishModificationCategory}
						setIDsShowInComboDishes={setIDsShowInComboDishes}
						IDsShowInComboDishes={IDsShowInComboDishes}
					/>
				</div>
				<BtnBlock />
			</Form>
		</div>
	);
};

export default InputFields;
